import { useState, useLayoutEffect, useRef, useEffect } from "react";
import { useStyles } from "./TakisHero.styles";
import Grid from "@material-ui/core/Grid";
import TakisButton from "../components/TakisButton";
import TakisPicture from "./TakisPicture";
import PropTypes from "prop-types";
import TakisHeroVideo from "./TakisHeroVideo";
import PauseHeroButton from "./PauseHeroButton";
import useResizeObserver from '@react-hook/resize-observer'

const TakisSlide = ({
  title,
  description,
  configuration,
  images,
  bgImages,
  cta,
  onMobile,
  userIsMoving,
  inlineVideos,
  setHeight,
  isActive,
  isProductPage
}) => {
  const classes = useStyles();

  const target = useRef(null)
  const [size, setSize] = useState()

  useLayoutEffect(() => {
    setSize(target.current.getBoundingClientRect())
  }, [target])

  useEffect(()=>{
    if(size && setHeight) {
      setHeight(size?.height)
    }
  }, [isActive, setHeight, size])

  // Where the magic happens
  useResizeObserver(target, (entry) => {
    setSize(entry.contentRect)
  })

  const hasButton = Object.keys(cta).length !== 0 && cta.constructor === Object;

  const [userStoppedVideo, setUserStoppedVideo] = useState(false)

  const alignmentsEnumeration = {
    center: "align_center",
    left: "align_left",
    right: "align_right",
    fullWidth: "full_width"
  }

  if (
    [
      alignmentsEnumeration.left,
      alignmentsEnumeration.right
    ]
    .includes(configuration)
  ) {
    // not centered Layout for align_left | algn_right
    return (
      <section
        className={`
          ${classes.slideContainer}
          ${ isActive ? 'isActive' : ''}
          ${ userIsMoving ? 'userIsMoving' : ''}
        `}
        ref={target}
        >
        <Grid container spacing={0} className={classes.gridContainer}>
          <Grid item xs={12} md={6} lg={7} className={classes.grid1}>
            <div className={classes.pad} />
            {bgImages && (
              <TakisPicture
                pictureClass={classes.hero_bg}
                images={bgImages}
                isLazy={false}
                aria-hidden={true}
              />
            )}
            {images && (
              <TakisPicture
                pictureClass={classes.hero_image}
                images={images}
                isLazy={false}
                aria-hidden={true}
                altText={title}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6} lg={5} className={classes.grid2}>
            <div className={classes.hero_data}>
              <h2 className={classes.hero_heading}>{title}</h2>
              {(
                <div
                  className={classes.hero_text}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
              {hasButton && (
                <TakisButton
                  color="yellowPurpleButton"
                  title={cta.title}
                  alt={cta.alt}
                  href={cta.href}
                  ariaLabel={cta.ariaLabel}
                />
              )}
            </div>
          </Grid>
        </Grid>
      </section>
    );
  } else if (configuration === alignmentsEnumeration.fullWidth) {
    // Full width layout
    return (
      <div
        className={`
          ${classes.slideContainer}
          ${ isActive ? 'isActive' : ''}
          ${ userIsMoving ? 'userIsMoving' : ''}
        `}
        ref={target}
        >
        {
          // loads Full width video Player and images
          inlineVideos &&
          inlineVideos.mobileVideo &&
          inlineVideos.desktopVideo && (
            <>
              <TakisHeroVideo
                bgImages={bgImages || null}
                inlineVideos={inlineVideos}
                className={classes.hero_bg}
                altText={title}
                isActive={isActive}
                ctaUrl={cta?.href}
                userPaused={userStoppedVideo}
                />
              {/* Play Pause */}
              <PauseHeroButton
                isPaused={!isActive || userStoppedVideo }
                isActive={isActive}
                onClick={() => setUserStoppedVideo(!userStoppedVideo)}
                />
            </>
          )
        }
      </div>
    );
  } else if (configuration === alignmentsEnumeration.center) {
    // Center
    return (
      <div
        className={`
          ${classes.slideContainer}
          ${ isActive ? 'isActive' : ''}
          ${ userIsMoving ? 'userIsMoving' : ''}
        `}
        ref={target}
        >
        {
          // loads Full width video Player and images
          inlineVideos &&
          inlineVideos.mobileVideo &&
          inlineVideos.desktopVideo && (
            <>
              <TakisHeroVideo
                bgImages={bgImages || null}
                inlineVideos={inlineVideos}
                className={classes.hero_bg}
                altText={title}
                isActive={isActive}
                ctaUrl={cta?.href}
                userPaused={userStoppedVideo}
              />
              {/* Play Pause */}
              <PauseHeroButton
                isPaused={!isActive || userStoppedVideo }
                isActive={isActive}
                onClick={() => setUserStoppedVideo(!userStoppedVideo)}
                />
            </>
          )
        }
        <Grid
          className={
            isProductPage
              ? classes.gridContainerProducts
              : classes.gridContainer
          }
          container
          spacing={0}
        >
          <Grid className={classes.center_grid1} item xs={12}>
            {images && (
              <TakisPicture
                pictureClass={
                  isProductPage
                    ? classes.takis_image_no_padding
                    : classes.takis_image_centered
                }
                images={images}
                isLazy={false}
                aria-hidden={true}
                altText={title}
              />
            )}
          </Grid>
        </Grid>
        <div
          className={`${classes.center_button} ${
            hasButton ? classes.no_display : ""
          }`}
        >
          {hasButton && (
            <TakisButton
              title={cta.title}
              href={cta.href}
              aria-hidden={!isActive}
            />
          )}
        </div>
      </div>
    );
  }
};

TakisSlide.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  configuration: PropTypes.string,
  images: PropTypes.array,
  bgImages: PropTypes.array,
  cta: PropTypes.object,
  onMobile: PropTypes.bool,
  inlineVideos: PropTypes.object,
  userIsMoving: PropTypes.bool,
  isProductPage: PropTypes.bool,
  isActive: PropTypes.bool,
  desktopVideo: PropTypes.string,
  mobileVideo: PropTypes.string,
};

export default TakisSlide;
